import * as R from 'remeda';
import {
  coordinates,
  type CoordinateTransformContext,
  doRectanglesIntersect,
  getRectangleIntersectionArea,
  type Rectangle2D,
} from '../../../canvas/core';
import type { PageLayoutData } from './types';

export const normalizeRotation = (degrees: number): number => {
  const normalized = degrees % 360;
  return normalized < 0 ? normalized + 360 : normalized;
};

/** @public */
export const rotateBy = (currentRotation: number, degrees: number): number =>
  normalizeRotation(currentRotation + degrees);

export const calculateViewportInContent = (
  transformCtx: CoordinateTransformContext
): Rectangle2D =>
  coordinates.rect.viewportToContent(transformCtx)({
    x: 0,
    y: 0,
    width: transformCtx.viewportRect.width,
    height: transformCtx.viewportRect.height,
  });

export const isPageIntersectingViewport = (
  page: PageLayoutData,
  viewport: Rectangle2D,
  margin = 0
): boolean =>
  doRectanglesIntersect(page, {
    x: viewport.x - margin,
    y: viewport.y - margin,
    width: viewport.width + 2 * margin,
    height: viewport.height + 2 * margin,
  });

// Gets index of first page that intersects minY vertically using binary search
export const findFirstPageInRange = (
  pages: readonly PageLayoutData[],
  minY: number
): number => {
  let startIdx = 0;
  let endIdx = pages.length;

  while (startIdx < endIdx) {
    const mid = Math.floor((startIdx + endIdx) / 2);
    const page = pages[mid];
    if (page && page.y + page.height < minY) {
      startIdx = mid + 1;
    } else {
      endIdx = mid;
    }
  }

  return startIdx;
};

// Gets only pages that intersect the range [minY, maxY]
export const getPagesInVerticalRange = (
  pages: readonly PageLayoutData[],
  minY: number,
  maxY: number
): readonly PageLayoutData[] =>
  R.pipe(
    pages,
    R.drop(findFirstPageInRange(pages, minY)),
    R.takeWhile(page => page.y <= maxY)
  );

interface PageVisibilityScore {
  page: PageLayoutData;
  visibleArea: number;
  index: number;
}

const TIE_THRESHOLD = 0.95;
const HYSTERESIS_FACTOR = 1.1;
const MIN_VISIBLE_RATIO = 0.03;

const calculateVisibilityScore = (
  page: PageLayoutData,
  index: number,
  viewport: Rectangle2D
): PageVisibilityScore => ({
  page,
  index,
  visibleArea: getRectangleIntersectionArea(page, viewport),
});

// When deciding which page is active, if there is a tie, let the best win, if not,
// switch if best's area is larger than current's up to a hysteresis factor (avoids flickering when exactly on the tie point)
const shouldSwitchFromCurrent = (
  bestCandidate: PageVisibilityScore,
  currentActiveScore: PageVisibilityScore,
  tieThreshold: number
): boolean => {
  const bestIsInTieGroup = bestCandidate.visibleArea >= tieThreshold;
  const currentIsInTieGroup = currentActiveScore.visibleArea >= tieThreshold;

  if (bestIsInTieGroup && currentIsInTieGroup) {
    return true;
  }

  return (
    bestCandidate.visibleArea >=
    currentActiveScore.visibleArea * HYSTERESIS_FACTOR
  );
};

export const findMostVisiblePage = (
  pages: readonly PageLayoutData[],
  viewport: Rectangle2D,
  currentActivePageId: number | null
): PageLayoutData | null =>
  R.pipe(
    pages,
    R.map((page, index) => calculateVisibilityScore(page, index, viewport)),
    R.filter(
      (score): score is PageVisibilityScore =>
        score.visibleArea >=
        viewport.width * viewport.height * MIN_VISIBLE_RATIO
    ),
    (scores): PageLayoutData | null => {
      if (scores.length === 0) return null;

      const maxVisibleArea = Math.max(...scores.map(s => s.visibleArea));
      const tieThreshold = maxVisibleArea * TIE_THRESHOLD;

      const bestCandidate = R.pipe(
        scores,
        R.filter(s => s.visibleArea >= tieThreshold),
        // prefer first in reading order
        R.firstBy(s => s.index)
      );

      if (!bestCandidate) return null;

      const currentActiveScore = scores.find(
        s => s.page.pageId === currentActivePageId
      );

      if (
        currentActiveScore &&
        bestCandidate.page.pageId !== currentActivePageId &&
        !shouldSwitchFromCurrent(
          bestCandidate,
          currentActiveScore,
          tieThreshold
        )
      ) {
        return currentActiveScore.page;
      }

      return bestCandidate.page;
    }
  );
